<template lang="pug">
  ProgramSettingsFormWrapper#registration-details(
    setting-type="settings"
    :form-data="formData"
    :disable-save.sync="disableSave"
    :keys-to-reset="keysToReset"
    @reset="$validator.reset()"
    title="Registration Settings"
  )
    ContentLoader(:height="70" v-if="!programLoaded")
      rect(x="0", y="0", rx="4", ry="4", width="48%", height="20")
      rect(x="50%", y="0", rx="4", ry="4", width="50%", height="20")
      rect(x="0", y="35", rx="4", ry="4", width="30%", height="10")
      rect(x="0", y="50", rx="4", ry="4", width="30%", height="20")
      rect(x="35%", y="35", rx="4", ry="4", width="30%", height="10")
      rect(x="35%", y="50", rx="4", ry="4", width="30%", height="20")
      rect(x="70%", y="35", rx="4", ry="4", width="30%", height="10")
      rect(x="70%", y="50", rx="4", ry="4", width="30%", height="20")
    template(v-else)
      .columns.is-mobile
        .column.is-two-fifths
          WtSwitchQuestionInput(
            label="Registration:"
            true-value-text="Open"
            false-value-text="Closed"
            v-model="allowRegistration"
            @input="disableSave = false"
            :disabled="registrationIsFull"
          )
          template(v-if="registrationIsFull")
            .has-text-danger Registration is full.
            a.add-more-seats.button.is-primary(href="mailto:hello@walkertracker.com") Contact Us To Add More Seats
      .is-block
        .columns.is-multiline.is-variable
          .column.is-half(v-if="$auth.user().isWtAdmin")
            WtSwitchQuestionInput(
              label="Registration Target Notifications:"
              :tooltip-text="registrationTargetTooltip"
              true-value-text="On"
              false-value-text="Off"
              v-model="registrationTargetEmails"
              @input="disableSave = isOriginalValue('registrationTargetEmails', !!registrationTargetEmails)"
              name="registrationTargetEmails"
            )
            br
            transition(name="fade")
            BField(
              :type="{ 'is-danger': errors.has('registration-target') }"
              :message="errors.first('registration-target')"
            )
              WtLabel(
                slot="label"
                label-for="registration-target"
                text="Registration Target"
              )
              BInput#registration-target(
                type="number"
                name="registration-target"
                v-model="registrationTarget"
                placeholder=""
                min="0"
                v-validate="{ required: registrationTargetEmails }"
                @input="disableSave = isOriginalValue('registrationTarget', registrationTarget)"
              )
            BField(
              :type="{ 'is-danger': errors.has('registration-target-email') }"
              :message="errors.first('registration-target-email')"
            )
              WtLabel(
                slot="label"
                label-for="registration-target-email"
                text="Notification Email"
              )
              BInput#registration-target-email(
                type="email"
                name="registration-target-email"
                v-model="registrationTargetEmailTo"
                placeholder="alert@site.com"
                v-validate="{ required: registrationTargetEmails, email: true }"
                @input="disableSave = isOriginalValue('registrationTargetEmailTo', registrationTargetEmailTo)"
              )

          .column.is-half
            WtSwitchQuestionInput(
              label="Require Employee ID:"
              tooltip-text="Require an id when members register to the site."
              v-model="requireEmployeeId"
              @input="disableSave = isOriginalValue('requireEmployeeId', !!requireEmployeeId)"
            )
            br
            transition(name="fade")
              div(v-if="requireEmployeeId")
                BField(
                  :type="{ 'is-danger': errors.has('employee-id-title') }"
                  :message="errors.first('employee-id-title')"
                )
                  WtLabel(
                    slot="label"
                    label-for="employee-id-title"
                    text="Employee ID Title"
                    tooltip-text="The label for the employee id field in registration."
                  )
                  BInput#employee-id-title(
                    type="text"
                    name="employee-id-title"
                    v-model="employeeIdTitle"
                    maxlength="75"
                    placeholder="Employee ID Description"
                    v-validate="{ required: requireEmployeeId }"
                    @input="disableSave = isOriginalValue('employeeIdTitle', employeeIdTitle)"
                  )
                BField(
                  :type="{ 'is-danger': errors.has('employee-id-description') }"
                  :message="errors.first('employee-id-description')"
                )
                  WtLabel(
                    slot="label"
                    label-for="employee-id-description"
                    text="Description"
                    tooltip-text="The description of the employee id field in registration so employees know what number to enter."
                  )
                  BInput#employee-id-description(
                    type="text"
                    name="employee-id-description"
                    v-model="employeeIdDescription"
                    maxlength="250"
                    placeholder="Please enter your employee id"
                    v-validate="{ required: requireEmployeeId }"
                    @input="disableSave = isOriginalValue('employeeIdDescription', employeeIdDescription)"
                  )
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { ContentLoader } from "vue-content-loader";
import { dateUtilityMixin } from "@/mixins/dateUtilityMixin";
import programSettingsFormMixin from "@/mixins/programSettingsFormMixin";
import WtSwitchQuestionInput from "@@/WtSwitchQuestionInput/WtSwitchQuestionInput";
import WtLabel from "@@/WtLabel/WtLabel";
import ProgramSettingsFormControls from "./ProgramSettingsFormControls";
import ProgramSettingsFormWrapper from "./ProgramSettingsFormWrapper";

export default {
  name: "RegistrationDetails",

  components: {
    WtSwitchQuestionInput,
    ProgramSettingsFormControls,
    ProgramSettingsFormWrapper,
    ContentLoader,
    WtLabel
  },

  mixins: [dateUtilityMixin, programSettingsFormMixin],

  computed: {
    ...mapGetters("program", [
      "coreProgramMeta",
      "programSettingsCopy",
      "programLoaded"
    ]),

    registrationIsFull() {
      let registrationTarget = this.$auth.user().program.registrationTarget;
      let memberCount = this.$auth.user().program.membersCount;

      if (!registrationTarget) return false;

      return registrationTarget <= memberCount;
    },

    registrationTargetTooltip() {
      return `Set your desired registration target, and we will send you an email notification when you
              are close to meeting your goal and again when your goal has been met.
              Don't want to go over the target? Close registration once your goal has been met.`;
    },

    formData() {
      return {
        allowRegistration: this.allowRegistration,
        registrationTargetEmails: this.registrationTargetEmails,
        registrationTargetEmailTo: this.registrationTargetEmailTo,
        registrationTarget: this.registrationTarget,
        // registrationStartDate: this.registrationStartDate,
        // registrationEndDate: this.registrationEndDate,
        requireEmployeeId: this.requireEmployeeId,
        employeeIdTitle: this.employeeIdTitle,
        employeeIdDescription: this.employeeIdDescription
      };
    },

    keysToReset() {
      return [
        "registrationTargetEmails",
        "registrationTargetEmailTo",
        "registrationTarget",
        // "registrationStartDate",
        // "registrationEndDate",
        "requireEmployeeId",
        "employeeIdTitle",
        "employeeIdDescription"
      ];
    },

    allowRegistration: {
      set(allowRegistration) {
        this.updateProgramSettingsCopy({
          key: "allowRegistration",
          value: allowRegistration
        });
      },
      get() {
        return Number(this.programSettingsCopy.allowRegistration);
      }
    },

    registrationTargetEmails: {
      set(registrationTargetEmails) {
        this.updateProgramSettingsCopy({
          key: "registrationTargetEmails",
          value: registrationTargetEmails
        });
      },
      get() {
        return Number(this.programSettingsCopy.registrationTargetEmails);
      }
    },

    registrationTargetEmailTo: {
      set(registrationTargetEmailTo) {
        this.updateProgramSettingsCopy({
          key: "registrationTargetEmailTo",
          value: registrationTargetEmailTo
        });
      },
      get() {
        return this.programSettingsCopy.registrationTargetEmailTo;
      }
    },

    registrationTarget: {
      set(registrationTarget) {
        this.updateProgramSettingsCopy({
          key: "registrationTarget",
          value: registrationTarget
        });
      },
      get() {
        return this.programSettingsCopy.registrationTarget;
      }
    },

    // registrationStartDate: {
    //   set(registrationStartDate) {
    //     // only set the date if it is different than the originally set value,
    //     // since Dates are objects, even if they are the same "date"
    //     // a comparison will evaluate to false and this will cause
    //     // an infinite loop
    //     if (
    //       this.datesMatch(
    //         registrationStartDate,
    //         this.programSettingsCopy.registrationStartDate
    //       )
    //     ) {
    //       return;
    //     }

    //     this.updateProgramSettingsCopy({
    //       key: "registrationStartDate",
    //       value: registrationStartDate
    //     });
    //   },
    //   get() {
    //     let date = null;
    //     const registrationStartDate = this.date(
    //       this.programSettingsCopy.registrationStartDate
    //     );

    //     if (registrationStartDate.isValid) {
    //       date = registrationStartDate.dateObject;
    //     }

    //     return date;
    //   }
    // },

    // registrationEndDate: {
    //   set(registrationEndDate) {
    //     if (
    //       this.datesMatch(
    //         registrationEndDate,
    //         this.programSettingsCopy.registrationEndDate
    //       )
    //     ) {
    //       return;
    //     }

    //     this.updateProgramSettingsCopy({
    //       key: "registrationEndDate",
    //       value: registrationEndDate
    //     });
    //   },
    //   get() {
    //     let date = null;
    //     const registrationEndDate = this.date(
    //       this.programSettingsCopy.registrationEndDate
    //     );

    //     if (registrationEndDate.isValid) {
    //       date = registrationEndDate.dateObject;
    //     }

    //     return date;
    //   }
    // },

    requireEmployeeId: {
      set(requireEmployeeId) {
        this.updateProgramSettingsCopy({
          key: "requireEmployeeId",
          value: requireEmployeeId
        });
      },
      get() {
        return Number(this.programSettingsCopy.requireEmployeeId);
      }
    },

    employeeIdTitle: {
      set(employeeIdTitle) {
        this.updateProgramSettingsCopy({
          key: "employeeIdTitle",
          value: employeeIdTitle
        });
      },
      get() {
        return this.programSettingsCopy.employeeIdTitle;
      }
    },

    employeeIdDescription: {
      set(employeeIdDescription) {
        this.updateProgramSettingsCopy({
          key: "employeeIdDescription",
          value: employeeIdDescription
        });
      },
      get() {
        return this.programSettingsCopy.employeeIdDescription;
      }
    }
  },

  watch: {
    registrationTargetEmails(newValue) {
      if (newValue && !this.registrationTargetEmailTo) {
        this.disableSave = true;
      }
      if (newValue && !this.registrationTarget) {
        this.registrationTarget = 10000;
      }
    },

    registrationTargetEmailTo(value) {
      if (
        (this.registrationTargetEmails && value === "") ||
        this.errors.any()
      ) {
        this.disableSave = true;
      }
    },

    registrationTarget(value) {
      if ((this.registrationTargetEmails && !value) || this.errors.any()) {
        this.disableSave = true;
      }
    },

    requireEmployeeId(value) {
      if (value && (!this.employeeIdTitle || !this.employeeIdDescription)) {
        this.disableSave = true;
      }
    },

    employeeIdTitle(value) {
      if ((this.requireEmployeeId && value === "") || this.errors.any()) {
        this.disableSave = true;
      }
    },

    employeeIdDescription(value) {
      if ((this.requireEmployeeId && value === "") || this.errors.any()) {
        this.disableSave = true;
      }
    }
  },

  methods: {
    ...mapActions("program", ["updateCoreMeta", "updateProgramSettingsCopy"])
  }
};
</script>

<style lang="sass" scoped></style>
